import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { IModalInformation, IUser, IUserCreateRequest, IUserEditRequest } from '../interfaces/user-definitions.interface';
import { BehaviorSubject, catchError, Observable, of, shareReplay, tap, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class UserDefinitionService {
    http = inject(HttpClient);
    baseUrl = environment.apiUrl + '/user-definitions/';

    $userInfo = new BehaviorSubject<IUser | null>(null);
    private userInfo$: Observable<IUser> | null = null;

    private currentUser = signal<IUser | null>(null);
    isSupplierAdmin = computed(() => this.currentUser()?.roles.some((role) => role.code === 'ROLE_SUPPLIER_ADMIN') ?? false);
    isAdmin = computed(() => this.currentUser()?.roles.some((role) => role.code === 'ROLE_ADMIN') ?? false);
    isAgentAdmin = computed(() => this.currentUser()?.roles.some((role) => role.code === 'ROLE_AGENT_ADMIN') ?? false);
    isAgentUser = computed(() => this.currentUser()?.roles.some((role) => role.code === 'ROLE_AGENT_USER') ?? false);
    isSubagentAdmin = computed(() => this.currentUser()?.roles.some((role) => role.code === 'ROLE_SUBAGENT_ADMIN') ?? false);
    isSubagentUser = computed(() => this.currentUser()?.roles.some((role) => role.code === 'ROLE_SUBAGENT_USER') ?? false);
    isUser = computed(() => this.currentUser()?.roles.some((role) => role.code === 'ROLE_USER') ?? false);
    isProviderSupplier = computed(() => this.currentUser()?.roles.some((role) => role.code === 'ROLE_PROVIDER_SUPPLIER') ?? false);

    private readonly financialRestrictedRoles = ['ROLE_AGENT_ADMIN', 'ROLE_AGENT_USER', 'ROLE_SUBAGENT_ADMIN', 'ROLE_SUBAGENT_USER'];
    canViewFinancials = computed(
        () => !(this.currentUser()?.roles.some((role) => this.financialRestrictedRoles.includes(role.code)) ?? false)
    );

    getUsers(): Observable<IUser[]> {
        const apiUrl = `${this.baseUrl}users`;
        return this.http.get<IUser[]>(apiUrl);
    }

    getUsersByFilter(wsUser: boolean, notOkUser: boolean): Observable<IUser[]> {
        const apiUrl = `${this.baseUrl}users?wsUser=${wsUser}&notOkUser=${notOkUser}`;
        return this.http.get<IUser[]>(apiUrl);
    }

    getModalInformation(): Observable<IModalInformation> {
        const apiUrl = `${this.baseUrl}modal-information`;
        return this.http.get<IModalInformation>(apiUrl);
    }

    createUser(userRequest: IUserCreateRequest) {
        const apiUrl = `${this.baseUrl}users`;
        return this.http.post(apiUrl, userRequest);
    }

    editUser(userRequest: IUserEditRequest) {
        const apiUrl = `${this.baseUrl}users`;
        return this.http.put(apiUrl, userRequest);
    }

    getUserInfo(forceRefresh = false): Observable<IUser> {
        if (forceRefresh) {
            this.clearUserInfo();
        }

        const cachedUser = this.$userInfo.getValue();
        if (cachedUser) {
            this.currentUser.set(cachedUser);
            return of(cachedUser);
        }

        if (!this.userInfo$) {
            const apiUrl = `${this.baseUrl}user-information`;
            this.userInfo$ = this.http.get<IUser>(apiUrl).pipe(
                tap((user) => {
                    this.$userInfo.next(user);
                    this.currentUser.set(user);
                }),
                catchError((error) => {
                    this.userInfo$ = null;
                    return throwError(() => error);
                }),
                shareReplay(1)
            );
        }

        return this.userInfo$;
    }

    clearUserInfo(): void {
        this.$userInfo.next(null);
        this.userInfo$ = null;
        this.currentUser.set(null);
    }
}
